// The world's content (phase C, gates C1 and C2): the layout row (content/tables/layout.json) and the scatter rules
// (content/tables/scatter.json) read into @rpg/world's shapes, with what the spike keeps beside them: each scatter
// piece's look, the surface colours and the streaming tiers' radii.
import type { Layout, ScatterRule } from "@rpg/world";
import type { SpikeContent } from "../content";

/** A scatter piece's look as the content gives it (colours are palette keys or hex). */
export interface PieceLookRow {
  kind: "tree" | "pine" | "bush" | "rock" | "tuft" | "flower";
  colours: string[];
  size?: number;
  variants?: number;
  /** How much its top sways as characters pass through (decision 0085): 0 rigid (the default), 1 fully. */
  sway?: number;
}

/** The streaming tiers' numbers (the layout row's `streaming`). */
export interface WorldStreaming {
  view: { request: number; inBy: number; unload: number };
  sim: { request: number; inBy: number; unload: number };
  budgetMs: number;
  lateBudgetMs: number;
  pinRadius: number;
  loadsPerStep: number;
  navMargin: number;
  predictS: number;
  teleportM: number;
}

/** Everything the world reads from the content. */
export interface WorldContent {
  layout: Layout;
  rules: ScatterRule[];
  looks: Record<string, PieceLookRow>;
  /** Surface tag (or "<region>.<tag>") → colours. */
  surfaces: Record<string, string[]>;
  streaming: WorldStreaming;
  /** Where the sky's light shafts stand (the layout row's `shafts`), if anywhere. */
  shafts?: WorldShafts;
  /** A kit piece's floor's surface tag, by piece (the layout row's `grounds`; improvement step 3, IMP-0026). */
  grounds?: Record<string, string>;
}

/** The shaft spots' rule (the layout row's `shafts`). */
export interface WorldShafts {
  region: string;
  spacing: number;
  side: number;
  length: [number, number];
  width: [number, number];
  max: number;
}

/** The layout row the world scene builds. */
export const WORLD_LAYOUT_ID = "green-valley";

/** Read the world's content. Throws naming what is missing. */
export function readWorldContent(spike: SpikeContent, id = WORLD_LAYOUT_ID): WorldContent {
  const reg = spike.registry;
  const row = reg.get("layout", id);
  if (!row) throw new Error(`world: no layout "${id}"`);
  const { reason: _r, surfaces, streaming, shafts, grounds, ...rest } = row as unknown as Layout & { reason: string; surfaces: Record<string, string[]>; streaming: WorldStreaming; shafts?: WorldShafts; grounds?: Record<string, string> };
  const layout = structuredClone(rest) as unknown as Layout;
  const rules: ScatterRule[] = [];
  const looks: Record<string, PieceLookRow> = {};
  for (const r of reg.rows("scatter")) {
    const { look, reason: _why, ...rule } = r as unknown as ScatterRule & { look: PieceLookRow; reason: string };
    rules.push(structuredClone(rule) as ScatterRule);
    looks[rule.piece] = structuredClone(look);
  }
  return { layout, rules, looks, surfaces: structuredClone(surfaces), streaming: structuredClone(streaming), ...(shafts ? { shafts: structuredClone(shafts) } : {}), ...(grounds ? { grounds: structuredClone(grounds) } : {}) };
}
